import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
} from '@cibli/shared';
import { useCallback, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import EmailDomainSuggestions from './EmailDomainSuggestions';

type EmailInputModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (email: string) => void;
  isLoading?: boolean;
};

const EmailInputModal = ({ open, onOpenChange, onSubmit, isLoading }: EmailInputModalProps) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const validateEmail = useCallback(
    (value: string): boolean => {
      if (!value.trim()) {
        setError(t('EmailInputModal.errorRequired'));
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        setError(t('EmailInputModal.errorInvalid'));
        return false;
      }
      setError('');
      return true;
    },
    [t],
  );

  const handleSubmit = useCallback(() => {
    if (validateEmail(email)) {
      onSubmit(email.trim());
      // Do not keep the typed address in memory after the modal closes
      setEmail('');
    }
  }, [email, validateEmail, onSubmit]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !isLoading) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit, isLoading],
  );

  const handleClose = useCallback(() => {
    setEmail('');
    setError('');
    onOpenChange(false);
  }, [onOpenChange]);

  // Inject the chosen domain and put the cursor at the end of the address.
  const handleSelectDomain = useCallback(
    (domain: string) => {
      const next = `${email}${domain}`;
      flushSync(() => {
        setEmail(next);
        setError('');
      });
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(next.length, next.length);
    },
    [email],
  );

  return (
    <AlertDialog open={open} onOpenChange={handleClose}>
      <AlertDialogContent className="sm:max-w-xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-2xl font-bold">
            {t('EmailInputModal.title')}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-lg">
            {t('EmailInputModal.description')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <Input
            id="email-input"
            // type="text" + inputMode: keeps the email keyboard but allows
            // setSelectionRange (unsupported on type="email") to place the
            // cursor at the end after a domain suggestion is inserted.
            type="text"
            inputMode="email"
            placeholder={t('EmailInputModal.placeholder')}
            value={email}
            onChange={e => {
              setEmail(e.target.value);
              if (error) setError('');
            }}
            onKeyDown={handleKeyDown}
            className="text-lg h-14 px-4"
            autoFocus
            ref={inputRef}
            maxLength={254}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            aria-describedby={error ? 'email-error' : undefined}
            aria-invalid={!!error}
            aria-label={t('EmailInputModal.title')}
          />
          {error && (
            <p id="email-error" role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <EmailDomainSuggestions value={email} onSelect={handleSelectDomain} />
        </div>
        <AlertDialogFooter className="gap-3">
          <Button variant="outline" size="lg" onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <Button size="lg" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? t('EmailInputModal.sending') : t('EmailInputModal.submit')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default EmailInputModal;
